import React, { useState } from 'react';
import { Button, Modal as AntModal, Form, Tag, Space, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckOutlined, CloseOutlined, ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, DragOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { PrimaryButton } from 'utils';
import { adminServices } from 'services';
import { AlertActions } from 'redux/actions';
import PromotionItemModal from './PromotionItemModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const StyledTable = styled.div`
  width: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 32px;
  .table-header, .table-row {
    display: flex;
    align-items: center;
    padding: 0 16px;
    min-height: 48px;
    border-bottom: 1px solid #f0f0f0;
  }
  .table-header {
    background: #f7f7f7;
    font-weight: 600;
    color: #333;
    font-size: 15px;
  }
  .table-row:last-child {
    border-bottom: none;
  }
  .cell {
    padding: 8px 0;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cell.id { width: 60px; flex: none; }
  .cell.actions { width: 180px; flex: none; display: flex; gap: 4px; justify-content: flex-end; }
`;

const ActionButton = styled(Button)`
  border: none;
  background: transparent;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const DragHandle = styled.span`
  cursor: grab;
  margin-right: 8px;
`;

const DescriptionPreview = styled.div`
  max-height: 100px;
  overflow: auto;
  border: 1px solid #f0f0f0;
  padding: 8px;
  border-radius: 4px;
  background: #fafafa;
  font-size: 12px;
`;

const PromotionItemsTable = ({ items, categories, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemImage, setItemImage] = useState(null);
  const [description, setDescription] = useState('');
  const [longDescription, setLongDescription] = useState('');
  const [form] = Form.useForm();
  const [orderMap, setOrderMap] = useState({});
  const [orderChanged, setOrderChanged] = useState(false);

  React.useEffect(() => {
    const map = {};
    (categories || []).forEach(cat => {
      map[cat.id] = [];
    });
    (items || []).forEach(item => {
      if (item.category && map[item.category.id]) {
        map[item.category.id].push(item);
      }
    });
    Object.keys(map).forEach(catId => {
      map[catId] = map[catId].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    setOrderMap(map);
    setOrderChanged(false);
  }, [items, categories]);

  const handleAdd = () => {
    setEditingItem(null);
    setItemImage(null);
    setDescription('');
    setLongDescription('');
    form.resetFields();
    setShowModal(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    setItemImage(record.image_url ? [record.image_url] : null);
    setDescription(record.description || '');
    setLongDescription(record.long_description || '');
    form.setFieldsValue({
      title: record.title,
      subtitle: record.subtitle,
      promotion_category_id: record.category?.id,
      green_flag: record.green_flag || false,
      is_suggested: record.is_suggested || false,
    });
    setShowModal(true);
    document.body.style.overflowY = 'hidden';
  };

  const handleDelete = async (id) => {
    AntModal.confirm({
      title: 'Item löschen',
      content: 'Sind Sie sicher, dass Sie dieses Item löschen möchten?',
      onOk: async () => {
        try {
          setLoading(true);
          await adminServices.deletePromotionItem(id);
          AlertActions.success('Item erfolgreich gelöscht!', 'Erfolg');
          onRefresh();
        } catch (error) {
          AlertActions.error(error.toString(), 'Fehler');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('subtitle', values.subtitle || '');
      formData.append('description', description);
      formData.append('long_description', longDescription);
      formData.append('promotion_category_id', values.promotion_category_id);
      formData.append('green_flag', values.green_flag || false);
      formData.append('is_suggested', values.is_suggested || false);
      // Handle image
      if (editingItem) {
        if (itemImage?.[0] && itemImage[0].thumbUrl) {
          formData.append('image', itemImage[0].originFileObj, itemImage[0].name);
        } else if (itemImage === null) {
          formData.append('image', null);
        }
      } else {
        if (itemImage?.[0]) {
          formData.append('image', itemImage[0].originFileObj);
        }
      }
      if (editingItem) {
        await adminServices.updatePromotionItem(editingItem.id, formData);
        AlertActions.success('Item erfolgreich aktualisiert!', 'Erfolg');
      } else {
        await adminServices.addPromotionItem(formData);
        AlertActions.success('Item erfolgreich hinzugefügt!', 'Erfolg');
      }
      setShowModal(false);
      document.body.style.overflowY = 'unset';
      onRefresh();
    } catch (error) {
      AlertActions.error(error.toString(), 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setItemImage(null);
    setDescription('');
    setLongDescription('');
    document.body.style.overflowY = 'unset';
  };

  // Drag & Drop
  const onDragEnd = (result, catId) => {
    if (!result.destination) return;
    const newList = Array.from(orderMap[catId]);
    const [removed] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, removed);
    setOrderMap({ ...orderMap, [catId]: newList });
    setOrderChanged(true);
  };

  const handleSaveOrder = async () => {
    setLoading(true);
    try {
      let orders = [];
      Object.keys(orderMap).forEach(catId => {
        orderMap[catId].forEach((item, idx) => {
          orders.push({ id: item.id, order: idx + 1 });
        });
      });
      await adminServices.updatePromotionItemsOrder({ orders });
      AlertActions.success('Reihenfolge erfolgreich gespeichert!', 'Erfolg');
      setOrderChanged(false);
      onRefresh();
    } catch (e) {
      AlertActions.error('Fehler beim Reihenfolge speichern', 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  const moveRow = async (catId, index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === orderMap[catId].length - 1)
    )
      return;
    const newList = Array.from(orderMap[catId]);
    const [removed] = newList.splice(index, 1);
    newList.splice(index + direction, 0, removed);
    setOrderMap({ ...orderMap, [catId]: newList });
    setOrderChanged(true);
    try {
      await adminServices.updatePromotionItemOrder(removed.id, { order: index + direction + 1 });
      onRefresh();
    } catch (e) {
      // ignore
    }
  };

  return (
    <>
      <HeaderWrapper>
        <Title>Promotion Items</Title>
        <Space>
          <PrimaryButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Neues Item
          </PrimaryButton>
          <PrimaryButton
            type="default"
            icon={<SaveOutlined />}
            onClick={handleSaveOrder}
            disabled={!orderChanged}
            loading={loading}
          >
            Reihenfolge speichern
          </PrimaryButton>
        </Space>
      </HeaderWrapper>
      {(categories || []).map((cat) => (
        <StyledTable key={cat.id}>
          <div style={{ background: '#f7f7f7', padding: '8px 16px', fontWeight: 600, fontSize: 16 }}>{cat.name}</div>
          <div className="table-header">
            <div className="cell id"></div>
            <div className="cell id">#</div>
            <div className="cell">Titel</div>
            <div className="cell">Untertitel</div>
            <div className="cell">Grün</div>
            <div className="cell">Vorgeschlagen</div>
            <div className="cell">Beschreibung</div>
            <div className="cell actions">Aktionen</div>
          </div>
          <DragDropContext onDragEnd={result => onDragEnd(result, cat.id)}>
            <Droppable droppableId={`items-droppable-${cat.id}`}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {(orderMap[cat.id] || []).map((item, idx) => (
                    <Draggable key={item.id} draggableId={item.id.toString()} index={idx}>
                      {(provided, snapshot) => (
                        <div
                          className="table-row"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            background: snapshot.isDragging ? '#f0f0f0' : '#fff',
                            ...provided.draggableProps.style,
                          }}
                        >
                          <div className="cell id">
                            <DragHandle {...provided.dragHandleProps}>
                              <DragOutlined />
                            </DragHandle>
                          </div>
                          <div className="cell id">{item.order}</div>
                          <div className="cell">{item.title}</div>
                          <div className="cell">{item.subtitle || '-'}</div>
                          <div className="cell">
                            <Tag
                              icon={item.green_flag ? <CheckOutlined /> : <CloseOutlined />}
                              color={item.green_flag ? 'success' : 'default'}
                            >
                              {item.green_flag ? 'Ja' : 'Nein'}
                            </Tag>
                          </div>
                          <div className="cell">
                            <Tag
                              icon={item.is_suggested ? <CheckOutlined /> : <CloseOutlined />}
                              color={item.is_suggested ? 'success' : 'default'}
                            >
                              {item.is_suggested ? 'Ja' : 'Nein'}
                            </Tag>
                          </div>
                          <div className="cell">
                            <DescriptionPreview dangerouslySetInnerHTML={{ __html: item.description }} />
                          </div>
                          <div className="cell actions">
                            <ActionButton
                              icon={<EditOutlined />}
                              onClick={() => handleEdit(item)}
                              title="Bearbeiten"
                            />
                            <ActionButton
                              icon={<DeleteOutlined />}
                              onClick={() => handleDelete(item.id)}
                              title="Löschen"
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </StyledTable>
      ))}
      <PromotionItemModal
        showModal={showModal}
        editingItem={editingItem}
        categories={categories}
        description={description}
        longDescription={longDescription}
        itemImage={itemImage}
        loading={loading}
        form={form}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        onDescriptionChange={setDescription}
        onLongDescriptionChange={setLongDescription}
        onImageChange={(image) => setItemImage(image.length ? image : null)}
        errorMessages={{
          requiredTitle: 'Titel ist erforderlich',
          requiredSubtitle: 'Untertitel ist erforderlich',
          requiredDescription: 'Beschreibung ist erforderlich',
          requiredCategory: 'Kategorie ist erforderlich',
          requiredImage: 'Bild ist erforderlich',
          saveError: 'Fehler beim Speichern',
          deleteError: 'Fehler beim Löschen',
          updateError: 'Fehler beim Aktualisieren',
          successAdd: 'Item erfolgreich hinzugefügt!',
          successUpdate: 'Item erfolgreich aktualisiert!',
          successDelete: 'Item erfolgreich gelöscht!',
          orderSaveError: 'Fehler beim Reihenfolge speichern',
          orderSaveSuccess: 'Reihenfolge erfolgreich gespeichert!'
        }}
      />
    </>
  );
};

export default PromotionItemsTable;
